// src/hooks/useNetworkQuality.js
import { useEffect, useState, useCallback } from 'react';

/**
 * Détecte la qualité du réseau en temps réel.
 * Utilise navigator.connection (Chrome/Edge/Android).
 * Fallback : considère la connexion comme rapide.
 *
 * Retourne :
 *  - isSlow: boolean       (2G ou 3G détectés)
 *  - isVerySlow: boolean   (2G détecté)
 *  - saveData: boolean     (mode économie de données)
 *  - effectiveType: string ('4g' | '3g' | '2g' | 'slow-2g' | 'unknown')
 *  - downlink: number      (Mbps estimés)
 */
const useNetworkQuality = () => {
  const [state, setState] = useState({
    isSlow: false,
    isVerySlow: false,
    saveData: false,
    effectiveType: 'unknown',
    downlink: 10,
  });

  const update = useCallback(() => {
    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!conn) {
      // Pas de support (Firefox / Safari desktop) → connexion supposée rapide
      setState({
        isSlow: false,
        isVerySlow: false,
        saveData: false,
        effectiveType: 'unknown',
        downlink: 10,
      });
      return;
    }

    const type = conn.effectiveType || 'unknown';
    const isSlow = ['slow-2g', '2g', '3g'].includes(type);
    const isVerySlow = ['slow-2g', '2g'].includes(type);

    setState({
      isSlow,
      isVerySlow,
      saveData: conn.saveData === true,
      effectiveType: type,
      downlink: conn.downlink || 0,
    });
  }, []);

  useEffect(() => {
    update();

    const conn =
      navigator.connection ||
      navigator.mozConnection ||
      navigator.webkitConnection;

    if (!conn) return;

    conn.addEventListener?.('change', update);
    return () => conn.removeEventListener?.('change', update);
  }, [update]);

  return state;
};

export default useNetworkQuality;